import { ApiContentSection, ApiSectionType } from '@storefronts/common-types'

const LCP_IMAGE_SECTION_TYPES = new Set<ApiSectionType>([
    ApiSectionType.CAROUSEL,
    ApiSectionType.INLINE_PDP,
    ApiSectionType.VIDEO_CAROUSEL,
])

/**
 * First section that paints a product/hero image. Later sections are treated
 * as below the fold for `loading="lazy"` / `fetchPriority="low"` (document
 * order, not viewport measurement).
 */
const PAINT_IMAGE_SECTION_TYPES = new Set<ApiSectionType>([
    ...LCP_IMAGE_SECTION_TYPES,
    ApiSectionType.PLP,
    ApiSectionType.PRODUCT_CARD,
    ApiSectionType.PERSONALIZED_PLP,
    ApiSectionType.UPSELL_BOX,
])

export const BELOW_FOLD_IMAGE_PROPS = {
    loading: 'lazy' as const,
    fetchPriority: 'low' as const,
}

/**
 * Index of the first section that may emit an LCP preload (Next/Image
 * `priority` for carousels/inline PDPs, or a video carousel's centered
 * thumbnail). Only one section per page should win so later image sections
 * do not compete. Returns -1 when none exist.
 */
export function getFirstLcpImageSectionIndex(
    sections: ApiContentSection[],
): number {
    return sections.findIndex((section) =>
        LCP_IMAGE_SECTION_TYPES.has(section.type),
    )
}

export function getFirstPaintImageSectionIndex(
    sections: ApiContentSection[],
): number {
    return sections.findIndex((section) =>
        PAINT_IMAGE_SECTION_TYPES.has(section.type),
    )
}

export function isSectionBelowFold(
    sectionIndex: number,
    firstPaintImageSectionIndex: number,
): boolean {
    return (
        firstPaintImageSectionIndex >= 0 &&
        sectionIndex > firstPaintImageSectionIndex
    )
}

export function shouldDeprioritizeCardImage({
    isBelowFold,
    cardIndex,
    firstVisibleCount,
}: {
    isBelowFold: boolean
    cardIndex: number
    firstVisibleCount: number
}): boolean {
    return isBelowFold || cardIndex >= firstVisibleCount
}

export function belowFoldImageProps(isBelowFold: boolean): {
    loading?: 'lazy'
    fetchPriority?: 'low'
} {
    return isBelowFold ? BELOW_FOLD_IMAGE_PROPS : {}
}
